import axios from '../plugins/axios'
import { Module } from 'vuex'

export interface Team {
    username: string
    token: string
}

const teamModule: Module<any, any> = {
    state: {
        workspace: {},
    },
    actions: {
        async initWorkspaceIndex() {
            if (this.workspaceIndexLoading) {
        		await this.initWorkspacePromise
        		return
        	}
            this.workspaceIndexLoading = true
            this.initWorkspacePromise = this.dispatch('initIndex', {
                id: 'workspace',
                idField: 'id',
                indexedFields: [
                    'name',
                    'subdomain',
                    'uuid',
                    'workspaceType',
                    'logo',
                ],
                source: '/api/workspaces',
                store: [
                    '@id',
                    '@type',
                    'id',
                    'uuid',
                    'name',
                    'subdomain',
                    'workspaceType',
                    'logo',
                    'website',
                    'country',
                    'customerId',
                    'timezone',
                    'isVersion2',
                ]
            })
            await this.initWorkspacePromise
            this.workspaceIndexLoading = false
            this.initWorkspacePromise = null
        },

        async getWorkspace(_, id) {
            if (id === undefined) {
                throw new Error('Invalid workspace ID specified')
            }

            const indexExists = await this.dispatch('indexExists', 'workspace')

            if (indexExists) {
                return await this.dispatch('getRecord', {
                    index: 'workspace',
                    id,
                })
            } else {
                return await this.dispatch('getWorkspaceFresh', id)
            }
        },

        async getWorkspaceFresh(_, id) {
            const indexExists = await this.dispatch('indexExists', 'workspace')

            const { data } = await axios.get('/api/workspaces' + (id ? '/' + id : ''))

            // Keep the local workspace list current when it's already loaded. Loading one workspace
            // doesn't load the whole list (up to 2,000 workspaces); that happens when a list or
            // search needs it (getWorkspaces).
            if (data && indexExists) {
                await this.dispatch('addRecord', {
                    index: 'workspace',
                    record: data,
                })
            }

            return data
        },

        async updateWorkspace(_, params) {
            const { data } = await axios.put(
                `/api/workspaces/${params.id}`,
                params,
            )

            return data
        },
        
        async updateWorkspaceName(_, params) {
            const { data } = await axios.put(
                `/api/workspaces/${params.id}/name`,
                params,
            )

            return data
        },

        async updateWorkspaceDetails(_, params) {
            const { data } = await axios.put(
                `/api/workspaces/${params.id}/details`,
                params,
            )

            return data
        },

        async deleteWorkspace(_, id ) {
            const { data } = await axios.delete(`/api/workspaces/${id}`)
            return data
        },

        // How many workspaces the user can see, without downloading them all.
        async getWorkspacesCount() {
            const { data } = await axios.get('/api/workspaces?page=1&itemPerPage=1')
            return data?.['hydra:totalItems'] ?? data?.['hydra:member']?.length ?? 0
        },

        async getWorkspaces(_, params) {
            const indexExists = await this.dispatch('indexExists', 'workspace')
            if (!indexExists || params?.refresh) {
                await this.dispatch('initWorkspaceIndex')
            }

            const sortNameAsc = (items) => {
                return items?.sort((a, b) =>
                    a.name?.toLowerCase() < b.name?.toLowerCase()
                        ? -1
                        : a.name?.toLowerCase() > b.name?.toLowerCase()
                        ? 1
                        : 0,
                )
            }

            const getSortOrder = () => {
                return sortNameAsc
            }

            let records = null
            records = await this.dispatch('getRecords', {
                index: 'workspace',
                term: params?.search,
                fields: ['name'],
                page: params?.page,
                perPage: params?.perPage,
                filters: [],
                sort: getSortOrder(),
            })

            return records
        },

        // Workspace settings > Website Analysis: buyer questions, objections and suggested topics
        async getWebsiteAnalysis() {
            const { data } = await axios.get('/api/website-analysis')
            return data
        },

        // { fillEmpty: true } only fills the lists (and key topics) that are empty
        async generateWebsiteAnalysis(_, params = {}) {
            const { data } = await axios.post('/api/website-analysis/generate', params)
            return data
        },

        // { kind (any Website Analysis list), title, detail, priority ('high' | 'medium' | 'low') }
        async addBuyerInsight(_, params) {
            const { data } = await axios.post('/api/website-analysis/insights', params)
            return data
        },

        // { id } and title and detail, and/or priority
        async editBuyerInsight(_, params) {
            const { data } = await axios.put(`/api/website-analysis/insights/${params.id}`, params)
            return data
        },

        async deleteBuyerInsight(_, id) {
            const { data } = await axios.delete(`/api/website-analysis/insights/${id}`)
            return data
        },

        // Workspace settings > Strategy > Business Objectives: { objectives: [...] }
        async getBusinessObjectives() {
            const { data } = await axios.get('/api/business-objectives')
            return data
        },

        // { title, goal }
        async addBusinessObjective(_, params) {
            const { data } = await axios.post('/api/business-objectives', params)
            return data
        },

        // { id } and any of title, goal, priority ('high' | 'medium' | 'low'), enabled
        async editBusinessObjective(_, params) {
            const { data } = await axios.put(`/api/business-objectives/${params.id}`, params)
            return data
        },

        async deleteBusinessObjective(_, id) {
            const { data } = await axios.delete(`/api/business-objectives/${id}`)
            return data
        },

        // Workspace settings > Strategy > Audience Mapping: { audiences: [...] }, the organization's
        // audience map. Silent: the page shows the API's reason (e.g. a name that's taken)
        async getOrganizationAudiences() {
            const { data } = await axios.get('/api/organization-audiences', { silent: true })
            return data
        },

        // { name, caresAbout, wants }
        async addOrganizationAudience(_, params) {
            const { data } = await axios.post('/api/organization-audiences', params, { silent: true })
            return data
        },

        // { id } and any of name, caresAbout, wants, enabled, deleted, domains ([{ key?, name, angle }])
        async editOrganizationAudience(_, { id, ...changes }) {
            const { data } = await axios.put(`/api/organization-audiences/${id}`, changes, { silent: true })
            return data
        },

        async deleteOrganizationAudience(_, id) {
            const { data } = await axios.delete(`/api/organization-audiences/${id}`, { silent: true })
            return data
        },

        // Workspace settings > Strategy > Strategic Messaging: { narrative, themes, topics, maxThemes }.
        // Silent: the page shows the API's reason (e.g. the theme cap)
        // Workspace settings > Strategy > Topics & Perspectives: { perspectives, topics, countDays, run }
        async getTopicPerspectives() {
            const { data } = await axios.get('/api/topic-perspectives', { cache: false, silent: true })
            return data
        },

        // A perspective: { topicId (or topicName, for a new Topic), text, proofPoints, priority }. A topic joins
        // the strategy with its first one
        async addTopicPerspective(_, params) {
            const { data } = await axios.post('/api/topic-perspectives', params, { silent: true })
            return data
        },

        // { id } and any of text, proofPoints, priority
        async editTopicPerspective(_, { id, ...changes }) {
            const { data } = await axios.put(`/api/topic-perspectives/${id}`, changes, { silent: true })
            return data
        },

        // A topic leaves the strategy with its last one
        async deleteTopicPerspective(_, id) {
            const { data } = await axios.delete(`/api/topic-perspectives/${id}`, { silent: true })
            return data
        },

        // Generate Perspective (Add / Edit popup): the AI drafts one on { topicId or topicName, text } - text,
        // when there is some, is what to improve on. Returns { text }
        async draftTopicPerspective(_, params) {
            const { data } = await axios.post('/api/topic-perspectives/draft', params, { silent: true })
            return data
        },

        // Find Proof (Add / Edit popup): the AI finds facts from the Website Analysis that back up
        // { topicId or topicName, text }. Returns { proofPoints }
        async findTopicPerspectiveProof(_, params) {
            const { data } = await axios.post('/api/topic-perspectives/proof', params, { silent: true })
            return data
        },

        // The AI suggests topics and drafts a perspective on each, in the background (run)
        async generateTopicPerspectives() {
            const { data } = await axios.post('/api/topic-perspectives/generate', {}, { silent: true })
            return data
        },

        async getStrategicMessaging() {
            const { data } = await axios.get('/api/strategic-messaging', { silent: true })
            return data
        },

        async saveStrategicNarrative(_, text) {
            const { data } = await axios.put('/api/strategic-messaging/narrative', { text }, { silent: true })
            return data
        },

        // { name, pointOfView, proofPoints, priority, enabled, topicIds, objectiveIds, audienceIds }
        async addStrategicTheme(_, params) {
            const { data } = await axios.post('/api/strategic-messaging/themes', params, { silent: true })
            return data
        },

        // { id } and any of the fields addStrategicTheme takes
        async editStrategicTheme(_, { id, ...changes }) {
            const { data } = await axios.put(`/api/strategic-messaging/themes/${id}`, changes, { silent: true })
            return data
        },

        async deleteStrategicTheme(_, id) {
            const { data } = await axios.delete(`/api/strategic-messaging/themes/${id}`, { silent: true })
            return data
        },

        // Generate: queues the AI's draft, which runs in the background (the response's run says how it's going)
        async generateStrategicMessaging() {
            const { data } = await axios.post('/api/strategic-messaging/generate', {}, { silent: true })
            return data
        },

        // A Topic the AI suggested for a theme: add it (creating the Topic) or dismiss it for good
        async acceptStrategicSuggestion(_, id) {
            const { data } = await axios.post(`/api/strategic-messaging/suggestions/${id}`, {}, { silent: true })
            return data
        },

        async dismissStrategicSuggestion(_, id) {
            const { data } = await axios.delete(`/api/strategic-messaging/suggestions/${id}`, { silent: true })
            return data
        },

        // Workspace settings > Strategy > Spokespeople (was Voice Mapping): { voices, themes, audiences, gaps, members }.
        // Silent: the page shows the API's reason
        async getVoiceMapping() {
            const { data } = await axios.get('/api/voice-mapping', { cache: false, silent: true })
            return data
        },

        // Switch a theme in a voice's Strategy: { memberId, themeId, enabled }
        async setVoiceTheme(_, params) {
            const { data } = await axios.put('/api/voice-mapping/themes', params, { silent: true })
            return data
        },

        // Switch an audience in a voice's Audience Mapping: { memberId, key, enabled }
        async setVoiceAudience(_, params) {
            const { data } = await axios.put('/api/voice-mapping/audiences', params, { silent: true })
            return data
        },

        // Add Expert: mark a member as a subject-matter expert
        async addVoiceExpert(_, memberId) {
            const { data } = await axios.post('/api/voice-mapping/experts', { memberId }, { silent: true })
            return data
        },

        // The Strategy checklist: { sections: [{ id, done, waiting, summary }], done, total }.
        // Silent: the checklist just shows without statuses when it fails
        async getStrategyProgress() {
            const { data } = await axios.get('/api/strategy/progress', { cache: false, silent: true })
            return data
        },

        // Workspace settings > Branding: the brand found on the workspace's website
        async getWorkspaceBrand() {
            const { data } = await axios.get('/api/workspace-brand')
            return data
        },

        // { colors: { primary, secondary, accent }, headingFont, bodyFont }; null clears one
        async updateWorkspaceBrand(_, params) {
            const { data } = await axios.put('/api/workspace-brand', params)
            return data
        },

        async analyzeWorkspaceBrand() {
            const { data } = await axios.post('/api/workspace-brand/analyze')
            return data
        },

        async getWorkspaceIntegration(_, params) {
            const page = 'page=' + (params?.page ?? 1)
            const perPage = params?.perPage
                ? '&itemPerPage=' + params.perPage
                : ''

            const { data } = await axios.get(
                '/api/workspaces_integration?' + 
                page + 
                perPage,
            )

            return data
        },

        // Workspace settings > Content Notifications (see the backend's
        // ContentNotificationsController).
        async getContentNotificationSettings() {
            const { data } = await axios.get('/api/content-notifications/settings')
            return data
        },

        async updateContentNotificationSettings(_, params) {
            const { data } = await axios.put('/api/content-notifications/settings', params)
            return data
        },

        async sendContentNotificationTest() {
            const { data } = await axios.post('/api/content-notifications/test')
            return data
        },

        // Connects Microsoft Teams: { slug: 'msteams', webhookUrl, frequency }
        async connectWorkspaceIntegration(_, params) {
            const { data } = await axios.post('/api/workspaces_integration', params)

            return data
        },

        // Slack (see the backend's SlackConnectionController): the install screen to send the
        // browser to, finishing it from the callback page, and disconnecting
        async startSlackConnect(_, { redirectUri }) {
            const { data } = await axios.post('/api/slack/connect', { redirectUri }, { silent: true })
            return data
        },

        async completeSlackConnect(_, { code, state, redirectUri }) {
            const { data } = await axios.post('/api/slack/connect/complete', { code, state, redirectUri }, { silent: true })
            return data
        },

        async disconnectSlack() {
            const { data } = await axios.post('/api/slack/disconnect', {}, { silent: true })
            return data
        },

        // Sends a sample new content alert: { channel: 'email' | 'slack' | 'msteams', email? }
        async sendTestAlert(_, params) {
            const { data } = await axios.post('/api/workspaces_integration/test', params)

            return data
        },

        async updateWorkspaceIntegration(_, params) {
            const { data } = await axios.put(
                `/api/workspaces_integration/${params.id}`, params,
            )

            return data
        },
    },
}

export default teamModule
